import env from "@/env.mjs";
import { useQueryState } from "@/hooks/useQueryState";
import { useGeo } from "@/i18n/routing";
import { formatProductCard } from "@/lib/centra/formatters";
import { depictFormatter } from "@/lib/depict/formatters";
import { depict } from "@/lib/depict/sdk";
import { DepictDisplay } from "@/lib/depict/types";
import { SearchSuggestionsResponseV3 } from "@depict-ai/types/api/SearchSuggestionsResponseV3";
import { useCentraClient, useToken } from "@frend-digital/centra/client";
import {
  DepictErrorMessage,
  DepictOptions,
} from "@frend-digital/depict/server";
import { useDebouncedValue } from "@frend-digital/ui/hooks";
import { Err, Ok } from "@frend-digital/utils/results";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { FetchError, ofetch } from "ofetch";
import { useMemo } from "react";

export const DEPICT_API_URL = "https://api.depict.ai";

export const fetchSuggestionsBySearch = async ({
  merchant,
  options,
}: {
  merchant: string;
  options: DepictOptions;
}): Promise<Ok<SearchSuggestionsResponseV3> | Err<DepictErrorMessage>> => {
  const { market, locale, query } = options;
  const url = `${DEPICT_API_URL}/v3/search/suggestions?merchant=${merchant}&market=${market}&locale=${locale}&query=${query}`;

  try {
    const res = await ofetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    return Ok<SearchSuggestionsResponseV3>(res);
  } catch (e) {
    if (e instanceof FetchError && e.response?.status === 422) {
      return Err<DepictErrorMessage>(e.response._data);
    }

    return Err<DepictErrorMessage>({
      detail: [
        {
          loc: ["unknown"],
          msg: (e as Error).message || "Unknown error",
          type: "unknown_error",
        },
      ],
    });
  }
};

export const SEARCH_QUERY_KEY = "q";

export const useSearchQuery = (initialValue: string) => {
  return useQueryState(SEARCH_QUERY_KEY, initialValue);
};

export const getSearchUrl = (query: string) => {
  const searchParams = new URLSearchParams();
  if (query) searchParams.set(SEARCH_QUERY_KEY, query);

  return `/search?${searchParams.toString()}`;
};

export const useSearchUrl = (query: string) => {
  return useMemo(() => getSearchUrl(query), [query]);
};
export const useProductSearch = (query: string, { enabled = true } = {}) => {
  const client = useCentraClient();
  const token = useToken();
  const debounced = useDebouncedValue(query, 150);
  const userData = useGeo();

  const depictOptions: DepictOptions = {
    market: userData?.country.toLowerCase()!,
    session_id: token ?? "token",
    locale: "en",
    limit: 4,
  };

  const {
    data: categoriesSuggestions,
    isLoading: isCategoriesSuggestionsLoading,
  } = useQuery<Ok<SearchSuggestionsResponseV3> | Err<DepictErrorMessage>>({
    queryKey: ["categories-suggestions", query],
    queryFn: async () => {
      const data = await fetchSuggestionsBySearch({
        merchant: env.NEXT_PUBLIC_DEPICT_MERCHANT,
        options: {
          market: userData?.country?.toLowerCase() ?? "no",
          locale: "en",
          query: query,
        },
      });

      return data;
    },
    refetchOnWindowFocus: false,
    enabled: !!enabled,
    placeholderData: keepPreviousData,
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["search", debounced],
    queryFn: async () => {
      const depictResult = await depict.search(query, depictOptions);

      if (depictResult.err)
        return {
          products: [],
          contentLinks: [],
        };
      const sortedProductIds = depictResult.val.displays
        .map((dis) => {
          const formattedDisplay = depictFormatter(
            dis as unknown as DepictDisplay,
          );
          return formattedDisplay.displayItemId;
        })
        .sort();

      const { data, error } = await client.POST("/products", {
        body: {
          relatedProducts: true,
          limit: 10,
          products: sortedProductIds,
        },
      });
      if (error)
        return {
          products: [],
          contentLinks: [],
        };

      return {
        products: data.products?.map(formatProductCard) || [],
        contentLinks: depictResult.val.content_search_links,
      };
    },
    placeholderData: keepPreviousData,
    enabled: !!(enabled && token),
  });

  return {
    data: data,
    isFetching,
    isLoading,
    isCategoriesSuggestionsLoading,
    categoriesSuggestions: categoriesSuggestions,
  };
};
